/**
 * Les sections du studio : Ongles, Coiffure, Perruque, Coupe garçon...
 * C'est ce qui organise le catalogue et les rapports.
 *
 * Pas de `Grid` ici : sur MUI v7 l'ancienne API (`item xs`) est silencieusement
 * ignorée. Les mises en page passent par du flex.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Typography, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Switch, FormControlLabel, CircularProgress, Alert, Tooltip,
} from '@mui/material';
import { Edit, Delete, Add, Category, Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { categoriePrestationAPI, extraireListe } from '../../../services/studioAPI';

const FORMULAIRE_VIDE = { nom: '', description: '', est_active: true };

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [dialogOuvert, setDialogOuvert] = useState(false);
  const [enEdition, setEnEdition] = useState(false);
  const [formulaire, setFormulaire] = useState(FORMULAIRE_VIDE);

  const charger = async () => {
    try {
      setLoading(true);
      setErreur(null);
      const reponse = await categoriePrestationAPI.getAll();
      setCategories(extraireListe(reponse));
    } catch (error) {
      const message = error.response?.data?.detail || error.message
        || 'Erreur lors du chargement des sections';
      setErreur(message);
      toast.error(message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const ouvrirDialog = (categorie = null) => {
    if (categorie) {
      setFormulaire({
        id: categorie.id,
        nom: categorie.nom,
        description: categorie.description || '',
        est_active: categorie.est_active,
      });
      setEnEdition(true);
    } else {
      setFormulaire(FORMULAIRE_VIDE);
      setEnEdition(false);
    }
    setDialogOuvert(true);
  };

  const enregistrer = async () => {
    if (!formulaire.nom.trim()) {
      toast.error('Le nom de la section est obligatoire');
      return;
    }
    try {
      if (enEdition) {
        await categoriePrestationAPI.update(formulaire.id, formulaire);
        toast.success('Section modifiée');
      } else {
        await categoriePrestationAPI.create(formulaire);
        toast.success('Section créée');
      }
      setDialogOuvert(false);
      charger();
    } catch (error) {
      toast.error(
        error.response?.data?.nom?.[0] || error.response?.data?.detail
        || "Erreur lors de l'enregistrement"
      );
    }
  };

  const basculerStatut = async (categorie) => {
    try {
      await categoriePrestationAPI.patch(categorie.id, { est_active: !categorie.est_active });
      toast.success(categorie.est_active ? 'Section masquée' : 'Section activée');
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  const supprimer = async (categorie) => {
    if (!window.confirm(`Supprimer la section « ${categorie.nom} » ?`)) return;
    try {
      await categoriePrestationAPI.delete(categorie.id);
      toast.success('Section supprimée');
      charger();
    } catch (error) {
      // PROTECT côté backend : une section qui porte des prestations ne part pas
      toast.error(
        error.response?.status === 400 || error.response?.status === 500
          ? 'Impossible : des prestations utilisent encore cette section'
          : (error.response?.data?.detail || 'Erreur lors de la suppression')
      );
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Sections du studio</Typography>
          <Typography variant="body2" color="text.secondary">
            {categories.length} section{categories.length > 1 ? 's' : ''} — elles regroupent
            les prestations et servent de filtre dans les rapports
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => ouvrirDialog()}>
          Nouvelle section
        </Button>
      </Box>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Section</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Prestations</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Category sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography color="text.secondary" mt={1}>
                    Aucune section — commencez par « Ongles » ou « Coiffure »
                  </Typography>
                </TableCell>
              </TableRow>
            ) : categories.map((categorie) => (
              <TableRow key={categorie.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{categorie.nom}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {categorie.description || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip size="small" label={categorie.nombre_prestations ?? 0} />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={categorie.est_active ? 'Active' : 'Masquée'}
                    color={categorie.est_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={categorie.est_active ? 'Masquer' : 'Activer'}>
                    <IconButton size="small" onClick={() => basculerStatut(categorie)}>
                      {categorie.est_active ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => ouvrirDialog(categorie)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton size="small" color="error" onClick={() => supprimer(categorie)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOuvert} onClose={() => setDialogOuvert(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{enEdition ? 'Modifier la section' : 'Nouvelle section'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nom de la section"
              value={formulaire.nom}
              onChange={(e) => setFormulaire({ ...formulaire, nom: e.target.value })}
              placeholder="Ongles, Coiffure, Perruque..."
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Description"
              value={formulaire.description}
              onChange={(e) => setFormulaire({ ...formulaire, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formulaire.est_active}
                  onChange={(e) => setFormulaire({ ...formulaire, est_active: e.target.checked })}
                />
              }
              label="Visible à la caisse"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOuvert(false)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrer}>
            {enEdition ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;
